import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Activity } from "lucide-react";
import { Sparkline } from "../components/Sparkline";
import { EmptyState, TableSkeleton } from "../components/states";
import { useDashboard, useServiceHealth } from "../lib/dashboard";

function formatNum(n: number): string {
  return n.toLocaleString("de-DE");
}

export function MissionControlPage() {
  const { data, isLoading, error } = useDashboard();
  const health = useServiceHealth();

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-8">
        <header className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Mission Control
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Übersicht
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {health.data && <HealthBadge health={health.data} />}
            {data && (
              <p className="font-mono text-xs text-text-tertiary">
                Stand {new Date(data.generatedAt).toLocaleTimeString("de-DE", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>
        </header>

        {error && (
          <div className="rounded-md border border-danger bg-danger-bg px-4 py-3 text-sm text-text-primary">
            Daten konnten nicht geladen werden. {String((error as Error).message)}
          </div>
        )}

        <ApprovalsCard
          loading={isLoading}
          count={data?.pendingApprovals?.count ?? 0}
        />

        <Section title="Aufmerksamkeit">
          {isLoading ? (
            <TableSkeleton rows={2} />
          ) : data?.attention && data.attention.length > 0 ? (
            <ul className="divide-y divide-border-secondary rounded-md border border-border-primary bg-bg-secondary">
              {data.attention.map((a) => (
                <li
                  key={`${a.type}-${a.projectId}`}
                  className="flex items-center gap-3 px-4 py-3 text-sm"
                >
                  <AlertTriangle size={14} className="text-warning" />
                  <Link
                    to={`/projects/${a.projectId}/overview`}
                    className="font-medium text-text-primary hover:text-text-link-hover"
                  >
                    {a.projectName}
                  </Link>
                  <span className="text-text-secondary">— {a.detail}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<CheckCircle2 size={16} className="text-success" />}
              title="Alles ruhig"
              body="Keine Projekte mit Aufmerksamkeitsbedarf."
            />
          )}
        </Section>

        <Section
          title="Top-3 Projekte (24h)"
          action={
            <Link
              to="/projects"
              className="inline-flex items-center gap-1 text-sm font-medium text-text-link hover:text-text-link-hover"
            >
              Alle Projekte <ArrowRight size={14} />
            </Link>
          }
        >
          {isLoading ? (
            <TableSkeleton rows={3} />
          ) : data?.topProjects && data.topProjects.length > 0 ? (
            <div className="overflow-hidden rounded-md border border-border-primary">
              <table className="w-full text-sm">
                <thead className="border-b border-border-primary bg-bg-secondary text-left">
                  <tr>
                    <th className="px-4 py-2.5 font-medium text-text-secondary">
                      Projekt
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-text-secondary">
                      Events 24h
                    </th>
                    <th className="px-4 py-2.5 font-medium text-text-secondary">
                      7 Tage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.topProjects.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-border-secondary last:border-0 hover:bg-bg-hover"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/projects/${p.id}/overview`}
                          className="font-medium text-text-primary hover:text-text-link-hover"
                        >
                          {p.name}
                        </Link>
                        {p.subdomain && (
                          <div className="font-mono text-xs text-text-tertiary">
                            {p.subdomain}.dhc-track.io
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-text-primary">
                        {formatNum(p.last24h)}
                      </td>
                      <td className="px-4 py-3">
                        <Sparkline
                          data={p.sparkline}
                          ariaLabel={`7-Tage-Trend für ${p.name}, Höhepunkt ${formatNum(Math.max(...p.sparkline))}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title="Noch keine Projekt-Daten"
              body="Sobald Events einlaufen, erscheinen hier die Top-Projekte."
            />
          )}
        </Section>
      </div>
    </div>
  );
}

function ApprovalsCard({ loading, count }: { loading: boolean; count: number }) {
  return (
    <Link
      to="/approvals"
      className="block rounded-md border border-border-primary bg-bg-secondary p-5 transition-colors hover:bg-bg-hover"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Approvals offen
          </p>
          <p className="mt-2 font-mono text-4xl font-semibold tabular-nums text-text-primary">
            {loading ? "—" : formatNum(count)}
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-text-link">
          Queue öffnen <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function HealthBadge({
  health,
}: {
  health: import("../lib/dashboard").ServiceHealthResponse;
}) {
  const isOk = health.status === "ok";
  const tooltip = buildHealthTooltip(health);
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${
        isOk
          ? "bg-success/15 text-success"
          : "bg-danger-bg text-danger animate-pulse"
      }`}
    >
      <Activity size={11} />
      {isOk ? "Health OK" : "Health degraded"}
    </span>
  );
}

function buildHealthTooltip(
  h: import("../lib/dashboard").ServiceHealthResponse,
): string {
  const lines: string[] = [];
  for (const s of h.services) {
    const ageSec = Math.floor(s.ageMs / 1000);
    lines.push(`${s.service}: ${s.status} (${ageSec}s ago)`);
  }
  if (h.bqSyncFailures24h.total > 0) {
    lines.push(`BQ-sync failures (24h): ${h.bqSyncFailures24h.total}`);
  }
  return lines.join(" · ") || "Keine Daten";
}

